import nanoid from "nanoid/non-secure";

import Profile from "./profile";
import Header from "./header";
import Search from "./search";
import Notifications from "./notifications";
import Chat from "./chat";
import WebSockets from "./websockets";
import CrossDomain from "./crossDomain";
import OutdatedBrowser from "./outdatedBrowser";
import FeedsStore from "./feed/feeds";
import FeedSettingsStore from "./feed/settings";
import ClientStorageStore from "./feed/clientStorage";
import WebPush from "./feed/webpush";
import Nps from "./nps";
import SecuritySurvey from "./securitySurvey";
import PopupController from "./popupController";
import Adaptive from "./adaptive";
import Enabler from "./enabler";
import LanguageInformer from "./languageInformer";
import AbTest from "./abTest";

import callWindowFunction from "../utils/callWindowFunction";
import { getSelectedLocale } from "../utils/locale";
import getEnvironDomain from "../utils/getEnvironDomain";
import getCookie from "../utils/getCookie";

import logger from "../logger";

import { THEMES } from "../consts";

class Store {
  constructor(
    baseUrl,
    profileUrl,
    notificationUrl,
    getWsTokenUrl,
    webSocketUrl,
    chatUrl
  ) {
    this.baseUrl = baseUrl || getEnvironDomain();
    this.theme = this.initTheme();
    this.locale = getSelectedLocale();
    this.profile = new Profile(this, profileUrl, baseUrl);
    this.crossDomain = new CrossDomain(this, baseUrl);
    this.header = new Header(this);
    this.search = new Search(this);
    this.notifications = new Notifications(this, notificationUrl);
    this.chat = new Chat(this, chatUrl);
    this.websockets = new WebSockets(this, getWsTokenUrl, webSocketUrl);
    this.outdatedBrowser = new OutdatedBrowser(this, baseUrl);
    this.feeds = new FeedsStore(this, notificationUrl);
    this.feedSettings = new FeedSettingsStore(this, notificationUrl);
    this.webPush = new WebPush(this);
    this.clientStorage = new ClientStorageStore(this);
    this.securitySurvey = new SecuritySurvey(this);
    this.nps = new Nps(this);
    this.enabler = new Enabler(this);
    this.popupController = new PopupController(this);
    this.adaptive = new Adaptive(this);
    this.languageInformer = new LanguageInformer(this, baseUrl, this.locale);
    this.abTest = new AbTest(this);

    this.initApp();
  }

  initTheme = () => {
    if (!process.env.PRERENDER) {
      return document?.body?.dataset.theme ?? THEMES.OLD_THEME;
    }

    return undefined;
  };

  registerStore = (name, storeCreateFunction) => {
    if (!this[name]) {
      this[name] = storeCreateFunction(this);
    }

    return this[name];
  };

  initApp = () => {
    try {
      callWindowFunction("listenEventBus", [
        nanoid(),
        this.crossDomain.eventBusMessageReceiver,
      ]);
    } catch (error) {
      console.error("Error while listenEventBus action. Error: ", error);
      logger.error(error, {
        sentryTitle: "Error while listenEventBus action",
        additionalStack: {
          isDominatrixFunctionInWindow: !!window.listenEventBus,
          error,
        },
      });
    }

    const shouldGetProfileByRequest = !!getCookie("getProfileByRequest");

    if (process.env.NODE_ENV !== "static") {
      this.profile.loadProfile({
        callbackForUser: () => {
          // Код далее будет вызван, если профиль будет успешно получен и это окажется пользователь
          try {
            this.notifications.reloadMuteOption();

            if (
              this.adaptive.isTablet ||
              this.adaptive.isSmallDesktop ||
              this.adaptive.isDesktop
            ) {
              setTimeout(() => {
                this.popupController.getPopupFeatures();
              }, 1000);
            }

            callWindowFunction(
              "subscribeToTabGroup",
              [
                this.profile.tabId,
                this.profile.group,
                this.crossDomain.becomeMasterAction,
                this.crossDomain.becomeSlaveAction,
                this.crossDomain.finalMasterAction,
              ],
              () => {
                this.crossDomain.becomeMasterAction();
              }
            );

            this.profile.getDocsCounters();

            this.languageInformer.checkVisibility();

            this.profile.enableFeed();
            this.feeds.initFeedSettings();

            this.header.initCookieBanner(true);

            this.securitySurvey.initSecuritySurvey();
          } catch (error) {
            console.error(
              "Error executing after logging actions. Error: ",
              error
            );
            logger.error(error, {
              sentryTitle: "Error executing after logging actions",
              additionalStack: {
                isDominatrixFunctionInWindow: !!window.subscribeToTabGroup,
                isMasterTab: this.crossDomain.isMaster,
                isNotificationSoundActive:
                  this.notifications.isNotificationSoundActive,
                nodeEnv: process.env.NODE_ENV,
                ntfIsActivated: this.feedSettings.ntfIsActivated,
                needToShowInvite: this.clientStorage.needToShowInvite,
                activatedUser: this.feedSettings.activatedUser,
                error,
              },
            });
          }
        },
        callbackForAnonim: () => {
          // Код далее будет вызван, если пользователь будет определен как аноним
          this.header.initCookieBanner(false);
        },
        shouldGetProfileByRequest,
      });
    }

    try {
      this.outdatedBrowser.checkOutdatedBrowserPopupVisibility();
    } catch (error) {
      console.error(
        "Error while checkOutdatedBrowserPopupVisibility. Error: ",
        error
      );
      logger.error(error, {
        sentryTitle: "Error while checkOutdatedBrowserPopupVisibility",
        additionalStack: {
          currentBrowser: this.outdatedBrowser.currentBrowser,
          showOutdatedBrowserPopup:
            this.outdatedBrowser.showOutdatedBrowserPopup,
          userAgent: navigator.userAgent,
          cookieValue: getCookie("outdatedBrowserInformer"),
          windowInnerWidth: window.innerWidth,
          error,
        },
      });
    }
  };
}

export default Store;
